import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/api';

interface UseGigSectorPrefsReturn {
  selectedSectors: string[];
  saving: boolean;
  setSectors: (sectors: string[]) => void;
}

/**
 * Loads and persists gig sector preferences from/to the backend
 * (`GET /api/profile/gig-sectors`, `PUT /api/profile/gig-sectors`).
 * Debounces saves by 600 ms so rapid UI changes don't flood the server.
 */
export function useGigSectorPrefs(): UseGigSectorPrefsReturn {
  const [selectedSectors, setSelectedSectors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    api.get('/profile/gig-sectors')
      .then(res => {
        const data: string[] = res.data?.data ?? [];
        setSelectedSectors(Array.isArray(data) ? data : []);
      })
      .catch(() => {});
  }, []);

  const setSectors = useCallback((sectors: string[]) => {
    setSelectedSectors(sectors);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await api.put('/profile/gig-sectors', { sectors });
      } catch {
        // silent — preferences are non-critical
      } finally {
        setSaving(false);
      }
    }, 600);
  }, []);

  return { selectedSectors, saving, setSectors };
}
